import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

// A table row's actions as one "Actions" button that opens them as a list.
//
// The list is drawn over the page (a portal), so a table that scrolls
// sideways cannot cut it off. It closes on a choice, a click outside,
// Escape, or when the page scrolls or is resized. Clicks inside never reach
// the row (so choosing an action does not also open the row).

// The actions given: buttons, possibly wrapped in a <div> or a fragment, or
// with false/null for those that do not apply.
function itemsOf(node) {
  return React.Children.toArray(node).flatMap((child) => {
    if (!React.isValidElement(child)) return typeof child === 'string' && child.trim() ? [child] : [];
    const wrapper = child.type === React.Fragment || child.type === 'div';
    return wrapper ? itemsOf(child.props.children) : [child];
  });
}

const MENU_WIDTH = 200;

export const ActionsMenu = ({ items, label = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Below the button, its right edge lined up; above it when there is no
  // room below; never off the left of the screen.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const height = menuRef.current ? menuRef.current.offsetHeight : 0;
    const below = r.bottom + 4 + height <= window.innerHeight;
    setPlace({
      top: below ? r.bottom + 4 : Math.max(8, r.top - 4 - height),
      left: Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => {
      if (menuRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') { close(); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onKeyDown={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 whitespace-nowrap"
      >
        {label}
        <ChevronDown size={13} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: place?.top ?? -9999, left: place?.left ?? -9999, width: MENU_WIDTH, zIndex: 1000 }}
          // A choice closes the list once its own handler has run. Seen on
          // the way down (capture), so a choice that stops its click from
          // bubbling still closes it; the row never hears about the click.
          onClickCapture={(e) => {
            const chosen = e.target.closest?.('button, a');
            if (chosen && !chosen.disabled) setTimeout(close, 0);
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="py-1 border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg
            [&_button]:!flex [&_button]:!w-full [&_button]:!justify-start [&_button]:!text-left [&_button]:!px-3 [&_button]:!py-1.5
            [&_button]:!text-xs [&_button]:!font-medium [&_button]:!bg-transparent [&_button]:!border-0 [&_button]:!shadow-none
            [&_button:hover]:!bg-gray-100 dark:[&_button:hover]:!bg-slate-700 [&_button:disabled]:!opacity-50
            [&_a]:!block [&_a]:!px-3 [&_a]:!py-1.5 [&_a]:!text-xs [&_a:hover]:!bg-gray-100 dark:[&_a:hover]:!bg-slate-700"
        >
          {items.map((item, i) => (
            <div key={i} role="none">{item}</div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

// What an Actions cell shows: nothing when no action applies, the one
// action as it is, or the "Actions" list for two or more.
export const ActionsCell = ({ children }) => {
  const items = itemsOf(children);
  if (!items.length) return null;
  if (items.length === 1) {
    return <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>{items[0]}</div>;
  }
  return <ActionsMenu items={items} />;
};

export default ActionsMenu;
